import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { checkoutStatus } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";

const PaymentSuccessPage = () => {
    const [params] = useSearchParams();
    const session_id = params.get("session_id");
    const navigate = useNavigate();
    const { clear } = useCart();
    const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
    const [info, setInfo] = useState(null);
    const attempts = useRef(0);

    useEffect(() => {
        if (!session_id) {
            setStatus("failed");
            return;
        }
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            try {
                const r = await checkoutStatus(session_id);
                setInfo(r);
                if (r.payment_status === "paid" && r.subscriptions_created) {
                    clear();
                    setStatus("paid");
                    return;
                }
                if (r.payment_status === "expired" || r.payment_status === "failed") {
                    setStatus("failed");
                    return;
                }
            } catch {}
            attempts.current += 1;
            if (attempts.current >= 8) {
                setStatus("timeout");
                return;
            }
            setTimeout(poll, 2000);
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [session_id, clear]);

    return (
        <div data-testid="payment-success-page" className="min-h-[100dvh] bg-[#0A0A0A] text-white flex flex-col items-center justify-center px-6">
            {status === "polling" && (
                <>
                    <Loader2 className="text-[#E1FF01] animate-spin" size={36} />
                    <p className="font-display text-2xl uppercase mt-4">Activating your plan…</p>
                    <p className="text-sm text-white/60 mt-1">Hold tight.</p>
                </>
            )}
            {status === "paid" && (
                <>
                    <div className="w-16 h-16 rounded-full bg-[#E1FF01] flex items-center justify-center mb-4">
                        <Sparkles className="text-[#0A0A0A]" size={28} strokeWidth={2.5} />
                    </div>
                    <p className="font-display text-4xl uppercase">You're locked in.</p>
                    <p className="text-sm text-white/60 mt-2 text-center">
                        {info?.subscription_ids?.length || 1} subscription
                        {(info?.subscription_ids?.length || 1) > 1 ? "s" : ""} active.
                    </p>
                    <button
                        data-testid="goto-subs"
                        onClick={() => navigate("/orders")}
                        className="mt-8 bg-[#E1FF01] text-[#0A0A0A] font-display text-lg uppercase tracking-wider px-6 py-3 rounded-md"
                    >
                        View My Subs
                    </button>
                </>
            )}
            {(status === "failed" || status === "timeout") && (
                <>
                    <XCircle size={40} className="text-[#FF3B30] mb-3" />
                    <p className="font-display text-2xl uppercase">
                        {status === "timeout" ? "Status check timed out" : "Payment failed"}
                    </p>
                    <p className="text-sm text-white/60 mt-2 text-center">
                        Try again from your cart, or reach out if you were charged.
                    </p>
                    <button
                        data-testid="back-home"
                        onClick={() => navigate("/")}
                        className="mt-8 bg-white text-[#0A0A0A] font-display text-lg uppercase tracking-wider px-6 py-3 rounded-md"
                    >
                        Back to Feed
                    </button>
                </>
            )}
        </div>
    );
};

export default PaymentSuccessPage;
